import { Table } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useGetSheetSvodObjectMutation } from "src/app/services/sheets";
import { ISvodObjectResponse, ISvodMetrics } from "src/app/services/sheets/type";
import { useTypedSelector } from "src/app/store";
import ContentTop from "src/layout/content/ContentTop";
import { svodObjectColumns, ISvodFlatRow } from "./constants";
import { MONTH_KEYS, getMonthLabel } from "../object/constants";
import { useTranslation } from "react-i18next";
import "../analyses.scss";

function SvodObjectPage() {
  const { t } = useTranslation();
  const [getSvodObject, { isLoading }] = useGetSheetSvodObjectMutation();
  const [data, setData] = useState<ISvodObjectResponse>();
  const { screenMode } = useTypedSelector((state) => state.layout);
  const [tableHeight, setTableHeight] = useState<number>();

  useEffect(() => {
    setTimeout(() => {
      setTableHeight(window.innerHeight - 180);
    }, 300);
  }, [screenMode]);

  useEffect(() => {
    getSvodObject()
      .unwrap()
      .then((res) => setData(res));
  }, []);

  const flattenMetrics = (metrics: ISvodMetrics) => ({
    installed_kw: metrics.installed_kw,
    materials: metrics.materials,
    installation: metrics.installation,
    logistics: metrics.logistics,
    other_production: metrics.other_production,
    office_expenses: metrics.office_expenses,
    total_cost: metrics.total_cost,
    volume: metrics.volume,
    profit_with_office_uzs: metrics.profit_with_office?.uzs ?? null,
    profit_with_office_usd: metrics.profit_with_office?.usd ?? null,
    profit_without_office_uzs: metrics.profit_without_office?.uzs ?? null,
    profit_without_office_usd: metrics.profit_without_office?.usd ?? null,
  });

  const tableData = useMemo(() => {
    if (!data) return [];
    const rows: ISvodFlatRow[] = [];

    // Year totals accumulator
    const yearTotal = {
      installed_kw: 0,
      materials: 0,
      installation: 0,
      logistics: 0,
      other_production: 0,
      office_expenses: 0,
      total_cost: 0,
      volume: 0,
      profit_with_office_uzs: 0,
      profit_with_office_usd: 0,
      profit_without_office_uzs: 0,
      profit_without_office_usd: 0,
    };
    const yearStation = { ...yearTotal };
    const yearBasket = { ...yearTotal };

    const addToAccum = (accum: typeof yearTotal, metrics: ISvodMetrics) => {
      accum.installed_kw += metrics.installed_kw || 0;
      accum.materials += metrics.materials || 0;
      accum.installation += metrics.installation || 0;
      accum.logistics += metrics.logistics || 0;
      accum.other_production += metrics.other_production || 0;
      accum.office_expenses += metrics.office_expenses || 0;
      accum.total_cost += metrics.total_cost || 0;
      accum.volume += metrics.volume || 0;
      accum.profit_with_office_uzs += metrics.profit_with_office?.uzs || 0;
      accum.profit_with_office_usd += metrics.profit_with_office?.usd || 0;
      accum.profit_without_office_uzs += metrics.profit_without_office?.uzs || 0;
      accum.profit_without_office_usd += metrics.profit_without_office?.usd || 0;
    };

    MONTH_KEYS.forEach((monthKey, idx) => {
      const month = data[monthKey];
      if (!month) return;

      // Accumulate year totals
      addToAccum(yearTotal, month.total);
      addToAccum(yearStation, month.station);
      addToAccum(yearBasket, month.basket);

      rows.push({
        key: `${monthKey}_total`,
        rowIndex: idx + 1,
        monthLabel: getMonthLabel(monthKey, t),
        category: getMonthLabel(monthKey, t),
        ...flattenMetrics(month.total),
        isMonthTotal: true,
      });
      rows.push({
        key: `${monthKey}_station`,
        monthLabel: "",
        category: t("sidebar.analyses.station"),
        ...flattenMetrics(month.station),
      });
      rows.push({
        key: `${monthKey}_basket`,
        monthLabel: "",
        category: t("sidebar.analyses.basket"),
        ...flattenMetrics(month.basket),
      });
    });

    // Add year total rows
    rows.push({
      key: "year_total",
      monthLabel: t("sidebar.analyses.yearTotal"),
      category: t("sidebar.analyses.yearTotal"),
      ...yearTotal,
      isMonthTotal: true,
      isYearTotal: true,
    });
    rows.push({
      key: "year_station",
      monthLabel: "",
      category: t("sidebar.analyses.station"),
      ...yearStation,
      isYearTotal: true,
    });
    rows.push({
      key: "year_basket",
      monthLabel: "",
      category: t("sidebar.analyses.basket"),
      ...yearBasket,
      isYearTotal: true,
    });

    return rows;
  }, [data, t]);

  const rowClassName = (record: ISvodFlatRow) => {
    const classes: string[] = [];
    if (record.isMonthTotal) classes.push("svod-month-total-row");
    if (record.isYearTotal) classes.push("svod-year-total-row");
    return classes.join(" ");
  };

  return (
    <div className="svod-page">
      <ContentTop title={t("sidebar.analyses.svodObject")} />

      <Table
        columns={svodObjectColumns(t)}
        dataSource={tableData}
        scroll={{ x: 2000, y: tableHeight }}
        bordered
        pagination={false}
        loading={isLoading}
        size="small"
        rowClassName={rowClassName}
      />
    </div>
  );
}

export default SvodObjectPage;
